import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useI18n } from '@/contexts/I18nContext';
import { authService } from '@/lib/auth.service';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ArrowRight, UserX, Edit, Mail, Phone, Shield, Calendar, Clock,
  CheckCircle2, AlertCircle, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { UserDetail } from '@/types/user.types';
import { EditUserModal } from '@/components/features/users/EditUserModal';

export default function UserDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tp } = useI18n();
  const { get, patch, loading } = useApi();

  const [user, setUser] = useState<UserDetail | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const isAdmin = authService.getUser()?.role === 'admin';

  useEffect(() => {
    if (id) fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchUser = async () => {
    setHasFetched(false);
    try {
      const result: any = await get(`/api/v1/dashboard/users/${id}`);
      setUser(result ?? null);
    } catch {
      setUser(null);
    } finally {
      setHasFetched(true);
    }
  };

  const handleMarkVerified = async () => {
    setVerifying(true);
    try {
      await patch(`/api/v1/dashboard/users/${id}`, { is_email_verified: true });
      toast.success(tp.users.updateSuccess);
      await fetchUser();
    } catch {
      // useApi already shows the error toast
    } finally {
      setVerifying(false);
    }
  };

  if (!hasFetched || (loading && !user)) {
    return (
      <PageContainer>
        <div className="space-y-4">
          <LoadingSkeleton type="card" rows={1} />
          <LoadingSkeleton type="card" rows={3} />
        </div>
      </PageContainer>
    );
  }

  if (!user) {
    return (
      <PageContainer>
        <EmptyState
          icon={<UserX className="h-16 w-16" />}
          title={tp.users.userNotFound}
          description={tp.users.userNotFoundDesc}
          action={{ label: tp.users.detailsBack, onClick: () => navigate('/dashboard/users') }}
        />
      </PageContainer>
    );
  }

  const roleLabels: Record<string, string> = {
    admin: tp.users.roleAdmin,
    carrier: tp.users.roleCarrier,
    driver: tp.users.roleDriver,
    tenant: tp.users.roleTenant,
    customer: tp.users.roleCustomer,
  };
  const initials = (user.name || user.email || '?').trim().charAt(0).toUpperCase();
  const formatDate = (v: string | null) =>
    v ? new Date(v).toLocaleDateString() : tp.users.neverLoggedIn;

  return (
    <PageContainer>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard/users')}
            className="h-9 w-9"
          >
            <ArrowRight className="h-5 w-5" />
          </Button>
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-lg font-bold text-primary">
            {initials}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-bold md:text-2xl">{user.name || '—'}</h1>
              <Badge variant="outline">{roleLabels[user.role] || user.role}</Badge>
              <Badge
                variant="outline"
                className={cn(
                  user.is_active
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-red-200 bg-red-50 text-red-700',
                )}
              >
                {user.is_active ? tp.users.statusActive : tp.users.statusInactive}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{user.email || '—'}</p>
          </div>
        </div>
        {isAdmin && (
          <Button variant="outline" onClick={() => setShowEdit(true)}>
            <Edit className="h-4 w-4 ms-2" />
            {tp.users.editButton}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Account information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{tp.users.accountInfoTitle}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <InfoRow icon={<Mail className="h-4 w-4" />} label={tp.users.tableHeaderEmail} value={user.email || '—'} />
            <InfoRow icon={<Phone className="h-4 w-4" />} label={tp.users.tableHeaderPhone} value={user.phone || '—'} />
            <InfoRow icon={<Shield className="h-4 w-4" />} label={tp.users.tableHeaderRole} value={roleLabels[user.role] || user.role} />
            <InfoRow icon={<Calendar className="h-4 w-4" />} label={tp.users.tableHeaderCreatedDate} value={formatDate(user.created_at)} />
            <InfoRow icon={<Clock className="h-4 w-4" />} label={tp.users.lastLoginLabel} value={formatDate(user.last_login)} />
          </CardContent>
        </Card>

        {/* Email verification */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{tp.users.emailVerificationTitle}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              {user.is_email_verified ? (
                <CheckCircle2 className="h-8 w-8 shrink-0 text-green-500" />
              ) : (
                <AlertCircle className="h-8 w-8 shrink-0 text-amber-500" />
              )}
              <div>
                <Badge
                  variant="outline"
                  className={cn(
                    user.is_email_verified
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : 'border-amber-200 bg-amber-50 text-amber-700',
                  )}
                >
                  {user.is_email_verified
                    ? tp.users.emailVerifiedBadge
                    : tp.users.emailNotVerifiedBadge}
                </Badge>
                <p className="mt-1 text-sm text-muted-foreground">
                  {user.is_email_verified
                    ? tp.users.emailVerifiedHint
                    : tp.users.emailNotVerifiedHint}
                </p>
              </div>
            </div>

            {isAdmin && !user.is_email_verified && (
              <Button onClick={handleMarkVerified} disabled={verifying} className="w-full">
                {verifying ? (
                  <Loader2 className="h-4 w-4 ms-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 ms-2" />
                )}
                {tp.users.markEmailVerifiedButton}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {user && (
        <EditUserModal
          open={showEdit}
          onClose={() => setShowEdit(false)}
          user={user}
          onSuccess={fetchUser}
        />
      )}
    </PageContainer>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between border-b py-2.5 last:border-0">
      <span className="flex items-center gap-2 text-sm text-muted-foreground">
        {icon}
        {label}
      </span>
      <span className="text-sm font-medium" dir="auto">{value}</span>
    </div>
  );
}
